import { TableContainer, Table, TableBody, TableRow, TableCell, Box, Grid, TableHead, Typography } from '@mui/material';
import { theme } from '../../../theme';
import StockCountQuantityInput from './StockCountQuantityInput';
import { StockCheck, StockCheckDetails, StockCheckItemValue } from '../../../app/services/api/generated';
import { TempCategoryItem } from './StockCountDetails';
import { useTranslation } from 'react-i18next';

type IProps = {
  printData: TempCategoryItem[],
  stockCheckDetails: StockCheckDetails,
  stockCheckDetail: StockCheck | undefined,
  selectedZoneId: string,
  allZonesEnabled: boolean,
  handleQuantityChange: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void,
  handleQuantityBlur: (itemId: number | undefined, valueId: number | undefined, newQuantityString: string) => void,

}

const PrintTable = ({ printData,
  stockCheckDetails,
  stockCheckDetail,
  selectedZoneId,
  allZonesEnabled,
  handleQuantityChange,
  handleQuantityBlur }: IProps) => {
  const { t } = useTranslation('common');

  const sortUoms = (itemValues: StockCheckItemValue[], primaryUom: string | undefined) => {
    return itemValues.sort((a, b) => {
      // If neither or both are 1, sort the rest by conversionToPrimary from lowest to highest
      if (primaryUom && a.uom == primaryUom) {
        return -1;
      }

      // First, put items with conversionToPrimary === 1 at the front
      if (a.conversionToPrimary === 1 && b.conversionToPrimary !== 1) {
        return -1;
      } else if (a.conversionToPrimary !== 1 && b.conversionToPrimary === 1) {
        return 1;
      }

      // If neither or both are 1, sort the rest by conversionToPrimary from lowest to highest
      if (a.conversionToPrimary !== undefined && b.conversionToPrimary !== undefined) {
        return a.conversionToPrimary - b.conversionToPrimary;
      }
      return 0;
    });
  };

  return (
    <Grid
      item
      id="print-section"
      style={{ display: 'none' }}
      className="print-section"
      xs={12}
      sx={{ mt: '20px' }}
    >
      {printData.map((zone: TempCategoryItem) => (
        <Box
          key={zone.name}
          display='flex'
          flexDirection='column'
          my={4}
          className="zone"
          marginTop='0'
        >
          <Box
            display="flex"
            flexDirection="row"
            p={4}
            className="title"
          >
            <Typography
              sx={{ mb: '4px', ml: '8px' }}
            >
              {zone.name}
            </Typography>
          </Box>
          <TableContainer component={'div'}
            id={`stock-items-table-container-${zone.name}`}
            className={'stockcount-table'}
            key={`${zone.name}`}
          >
            <Table
              size="small"
              aria-label="a table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width:'8px', padding:'0' }}></TableCell>
                  <TableCell 
                    align="left">
                    {t('rowNumber')}
                  </TableCell>
                  <TableCell 
                    align="left"
                  >
                    {t('skuNumber')}
                  </TableCell>
                  <TableCell
                    align="left">
                    {t('stockOnHand.category')}
                  </TableCell>
                  <TableCell
                    align="left"
                    className='zone-table-cell'>
                    {t('skuDescription')}
                  </TableCell>
                  <TableCell
                    align="left"
                  >
                    {t('quantity')}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {zone.items.map((item) => (
                  <TableRow
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 }, borderBottom: '1px solid',
                      borderBottomColor: theme.palette.custom.gray[200],
                      backgroundColor: theme.palette.custom.gray[50],
                    }}
                    className='item'
                    key={`${zone.name}-${item.id}`}
                  >
                    <TableCell sx={{ width:'8px', padding:'0' }}></TableCell>
                    <TableCell>{item.rowNumber}</TableCell>
                    <TableCell>
                      {item.itemNumber}
                    </TableCell>
                    <TableCell>
                      {item.category}
                    </TableCell>
                    <TableCell sx={{ width: '2x' }}
                      className='zone-table-cell'>
                      {item.description}
                    </TableCell>
                    <TableCell className="quantity-column"
                      sx={{ width: '3x', height: '60px' }}>
                      {item.itemValues && (
                        <div className="quantity-fields-wrapper"
                          style={{ display: 'flex', flexWrap: 'wrap', height: '100%', gap: '8px' }}>
                          {sortUoms(item.itemValues, item.primaryUom).map((value, index) => (
                            <div key={index}
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                minWidth: '90px',
                                justifyContent: 'flex-start',
                                gap: '4px',

                              }}>
                              <StockCountQuantityInput
                                item={item}
                                value={value}
                                stockCheckDetail={stockCheckDetail}
                                selectedZoneId={selectedZoneId}
                                allZonesEnabled={allZonesEnabled}
                                handleQuantityChange={handleQuantityChange}
                                handleQuantityBlur={handleQuantityBlur}

                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Grid>
  );
};

export default PrintTable;